// A script loaded on a public page keeps running after the app moves to a signed in page without a reload.
// So when that happens, the app opens the signed in page as a new page, and nothing loaded before follows.

import { authStore } from '@/features/auth/store/authStore';

import { HUBSPOT_SCRIPT_ID } from '../hubspotConsent';
import type { MetaPixelWindow } from '../metaPixel';
import { isPublicPath } from '../publicPages';

type NavigationRouter = {
  subscribe: (
    eventType: 'onBeforeLoad',
    fn: (event: { toLocation: { pathname: string; href: string } }) => void
  ) => () => void;
};

// tracking scripts that only public pages load
const TRACKING_SCRIPT_IDS = [HUBSPOT_SCRIPT_ID];

function trackingLoaded(): boolean {
  // the Pixel counts once installed: a later yes would download Meta's script on the signed in page
  return (
    Boolean((window as MetaPixelWindow).pfMetaPixelLoaded) ||
    TRACKING_SCRIPT_IDS.some((id) => document.getElementById(id) !== null)
  );
}

/**
 * Reloads into the first signed in page when a tracking script is loaded. A signed out visitor is only
 * passing through on the way to sign in, so nothing is reloaded for them. Returns a stop.
 */
export function startTrackingBoundary(
  router: NavigationRouter,
  reload: (href: string) => void = (href) => window.location.assign(href),
  isSignedIn: () => boolean = () => authStore.getState().isAuthenticated()
): () => void {
  return router.subscribe('onBeforeLoad', ({ toLocation }) => {
    try {
      if (!isPublicPath(toLocation.pathname) && isSignedIn() && trackingLoaded()) {
        reload(toLocation.href);
      }
    } catch {
      // navigation must never break
    }
  });
}
